import { useState, useEffect } from 'react'
import { Path, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { GridHookForm, Select } from 'vnetwork-ui'
import { Contract } from '@/modules/Contract/types'
import PriceRangesList from '../PriceRangesList'

const DEFAULT_PRICES = [
    {
        price_start: 0,
        price_end: 5,
        price_per_unit: 100,
    },
    {
        price_start: 5,
        price_end: 10,
        price_per_unit: 90,
    },
    {
        price_start: 10,
        price_end: 999999999,
        price_per_unit: 80,
    },
]

interface PricingSectionProps {
    name: string
    index: number
    formControl: UseFormReturn<Contract>
    commitmentValue?: number
    forcePricingType?: 'single' | 'range' // If provided, skip the selector
}

const PricingSection: React.FC<PricingSectionProps> = ({
    name,
    index,
    formControl,
    commitmentValue = 0,
    forcePricingType,
}) => {
    const { t } = useTranslation()
    const [pricingType, setPricingType] = useState<'single' | 'range'>(
        forcePricingType || 'single'
    )

    const prices =
        formControl.watch(`${name}.[${index}].prices` as Path<Contract>) || []

    // Handle pricing type change
    const handlePricingTypeChange = (type: 'single' | 'range') => {
        setPricingType(type)

        if (type === 'single') {
            // Set single price: 0 to unlimited
            formControl.setValue(
                `${name}.[${index}].prices` as Path<Contract>,
                [
                    {
                        price_start: 0,
                        price_end: 999999999,
                        price_per_unit: 0,
                    },
                ]
            )
        } else {
            // Set default ranges
            formControl.setValue(
                `${name}.[${index}].prices` as Path<Contract>,
                DEFAULT_PRICES
            )
        }
    }

    useEffect(() => {
        // If forcePricingType is provided, use it and don't auto-detect
        if (forcePricingType) {
            setPricingType(forcePricingType)
            return
        }

        // Auto-detect current pricing type based on prices
        if (
            prices.length === 1 &&
            prices[0]?.price_start === 0 &&
            prices[0]?.price_end === 999999999
        ) {
            setPricingType('single')
        } else if (prices.length > 1) {
            setPricingType('range')
        }
    }, [prices, forcePricingType])

    return (
        <div className="space-y-0" data-tour="bandwidth-overage-pricing">
            <GridHookForm
                subForm
                className="grid-cols-3"
                data={[
                    // Only show pricing type selector if not forced
                    ...(!forcePricingType
                        ? [
                              {
                                  className:
                                      pricingType === 'single'
                                          ? 'bg-white rounded border border-slate-200/60'
                                          : 'col-span-3 bg-white rounded border border-slate-200/60',
                                  renderEditComponent: () => (
                                      <Select
                                          value={pricingType}
                                          onChange={(value) =>
                                              handlePricingTypeChange(
                                                  value as 'single' | 'range'
                                              )
                                          }
                                          allowClear={false}
                                          options={[
                                              {
                                                  value: 'single',
                                                  label: t(
                                                      'contract.single_price'
                                                  ),
                                              },
                                              {
                                                  value: 'range',
                                                  label: t(
                                                      'contract.range_price'
                                                  ),
                                              },
                                          ]}
                                          placeholder={t(
                                              'contract.select_pricing_type'
                                          )}
                                          size="small"
                                      />
                                  ),
                              },
                          ]
                        : []),
                    // Single price input
                    {
                        formItemProps: {
                            name: `${name}.[${index}].prices.[0].price_per_unit` as Path<Contract>,
                            type: 'number',
                            label: t('price_per_unit'),
                            placeholder: '0',
                        },
                        className:
                            pricingType === 'single'
                                ? !forcePricingType
                                    ? 'col-span-2 bg-white rounded border border-slate-200/60'
                                    : 'col-span-3 bg-white rounded border border-slate-200/60'
                                : 'hidden',
                    },
                ]}
                formControl={formControl}
                actions={[]}
            />
            {pricingType === 'range' && (
                <div className="bg-white p-3 rounded border border-slate-200/60">
                    <PriceRangesList
                        namingControl={`${name}.[${index}].prices`}
                        formControl={formControl}
                        commitmentValue={Number(commitmentValue) || 0}
                    />
                </div>
            )}
        </div>
    )
}

export default PricingSection
